import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { verifyNavigationToken, isAuthenticated } from '../lib/api-scanned-cards';

interface User {
  userId: string;
  email?: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Check for navigation token in URL (when coming from main app)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const navToken = params.get('nav_token');

    if (navToken) {
      handleNavigationToken(navToken);
    }
  }, []);

  async function handleNavigationToken(token: string) {
    try {
      setLoading(true);
      setError(null);

      const result = await verifyNavigationToken(token);

      // Store the session token
      localStorage.setItem('token', result.token);
      setUser(result.user);

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err: any) {
      console.error('Navigation token verification failed:', err);
      localStorage.removeItem('token');
      setUser(null);
      setError(err.message || 'Authentication failed. Please try logging in from the main app.');
    } finally {
      setLoading(false);
    }
  }

  async function checkAuth() {
    const token = localStorage.getItem('token');

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Decode the token to get user info
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(parts[1]));

      // Check if token is expired
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem('token');
        setUser(null);
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }

      setUser({
        userId: payload.userId || payload.sub || payload.eurekaUserId,
        email: payload.email,
        name: payload.name || payload.displayName
      });
    } catch (err) {
      console.error('Token validation failed:', err);
      localStorage.removeItem('token');
      setUser(null);
    }

    setLoading(false);
  }

  async function login(token: string) {
    return handleNavigationToken(token);
  }

  function logout() {
    localStorage.removeItem('token');
    setUser(null);
    // Redirect to main app
    window.location.href = 'https://eureka-factory.com/scanned-cards';
  }

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
